import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ProjectConfigForm } from "./ProjectConfigForm";
import { Project } from "@/services/api/types";
import { projectsService } from "@/services/api/projects.service";
import { toast } from "sonner";
import { ScrollArea } from "@/components/ui/scroll-area";

interface ProjectDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  project?: Project;
  onSuccess?: () => void;
}

export const ProjectDialog: React.FC<ProjectDialogProps> = ({
  open,
  onOpenChange,
  project,
  onSuccess,
}) => {
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (data: {
    name: string;
    description?: string;
    project_type?: string;
    status?: "planned" | "active" | "on-hold" | "completed";
    priority?: "high" | "medium" | "low";
    start_date?: string;
    target_end_date?: string;
    milestones?: Array<{ name: string; date: string }>;
    department?: string;
    budget?: number | "";
    currency?: string;
    billing_type?: string;
  }) => {
    setIsLoading(true);
    try {
      // Clean up the data - convert empty strings to undefined
      const cleanedData = {
        ...data,
        budget: data.budget === "" ? undefined : data.budget,
      };

      if (project) {
        // Update existing project
        await projectsService.update(project.id, cleanedData);
        toast.success("Project updated successfully");
      } else {
        // Create new project
        await projectsService.create(cleanedData);
        toast.success("Project created successfully");
      }
      onOpenChange(false);
      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      console.error("Error saving project:", error);
      toast.error(
        error instanceof Error
          ? error.message
          : "Failed to save project. Please try again.",
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] p-0">
        <DialogHeader className="px-6 pt-6 pb-4">
          <DialogTitle>
            {project ? "Edit Project" : "Create New Project"}
          </DialogTitle>
          <DialogDescription>
            {project
              ? "Update your project configuration"
              : "Configure your new project with all the necessary details"}
          </DialogDescription>
        </DialogHeader>
        <ScrollArea className="max-h-[calc(90vh-8rem)] px-6">
          <ProjectConfigForm
            project={project}
            onSubmit={handleSubmit}
            onCancel={handleCancel}
            isLoading={isLoading}
          />
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
};
